import React, {
    FC,
    useEffect,
    useState,
    useRef,
    useCallback,
    PropsWithChildren,
    useLayoutEffect,
    useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { useSelector } from 'src/modules/useSelector';

import styles from 'src/components/Uxfeedback/uxfeedback.less';

enum MessageType {
    Style = 'style',
    Image = 'image',
    Opacity = 'opacity',
    Class = 'class',
    Size = 'size',
    Info = 'info',
    Scroll = 'scroll',
    Position = 'position',
    Listener = 'listener',
    Event = 'event',
    History = 'history',
    LocationListener = 'locationListener',
    AddScroll = 'addScroll',
    RemoveScroll = 'removeScroll',
    ShowWidgetIcon = 'showWidgetIcon',
    ClientHeight = 'clientHeight',
    Properties = 'properties',
}

type MessagePayloadType = null | string | number | Record<string, unknown>;

interface MessageData {
    type: MessageType;
    payload: MessagePayloadType;
}

interface UxfeedbackProps {
    iframeSrc: string;
    iframeOrigin: string;
    sendEventsOnLoad?: string[];
}

const isString = (payload: MessagePayloadType): payload is string => {
    return typeof payload === 'string';
};

const isNumber = (payload: MessagePayloadType): payload is number => {
    return typeof payload === 'number';
};

const isSize = (payload: MessagePayloadType): payload is { width?: string; height?: string } => {
    return payload !== null && typeof payload === 'object' && !!(payload.width || payload.height);
};

const setIframeStyle = (iframeRef: React.RefObject<HTMLIFrameElement>, style: string) => {
    iframeRef?.current?.setAttribute('style', style);
};

const sendMessage = (iframeRef: React.RefObject<HTMLIFrameElement>, type: MessageType, payload: MessagePayloadType) => {
    iframeRef?.current?.contentWindow?.postMessage({ type: `uxfb/${type}`, payload }, '*');
};

const showWidgetIcon = (iframeRef: React.RefObject<HTMLIFrameElement>, width: string, height: string) => {
    if (iframeRef && iframeRef.current) {
        iframeRef.current.setAttribute('width', width);
        iframeRef.current.setAttribute('height', height);
        iframeRef.current.className = '';
        iframeRef.current.style.opacity = '1';
    }
    sendMessage(iframeRef, MessageType.ShowWidgetIcon, {});
};

const setIframeOpacity = (iframeRef: React.RefObject<HTMLIFrameElement>, opacity: number) => {
    if (iframeRef && iframeRef.current) {
        iframeRef.current.style.opacity = `${opacity}`;
    }
};

const addClassToIframe = (iframeRef: React.RefObject<HTMLIFrameElement>, className: string) => {
    if (iframeRef && iframeRef.current) {
        iframeRef.current.classList.add(className);
    }
};

const setIframeSize = (iframeRef: React.RefObject<HTMLIFrameElement>, width: string, height: string) => {
    if (iframeRef && iframeRef.current) {
        if (width) {
            iframeRef.current.style.width = width;

            if (width === '100%') {
                iframeRef.current.style.left = '';
                iframeRef.current.style.right = '';
                iframeRef.current.style.top = '';
                iframeRef.current.style.transform = '';
            }
        }

        if (height) {
            iframeRef.current.style.height = height;
        }
    }
};

const setIframePosition = (iframeRef: React.RefObject<HTMLIFrameElement>, heightString: string) => {
    const height = parseInt(heightString, 10);

    const clientHeight = window.innerHeight || document.documentElement.clientHeight;
    const middle = (clientHeight - height) / 2;
    const top = height >= clientHeight ? 0 : middle;

    if (iframeRef && iframeRef.current) {
        iframeRef.current.style.transform = '';
        iframeRef.current.style.left = '';
        iframeRef.current.style.right = '';
        iframeRef.current.style.top = `${top}px`;
    }
};

const sendCurrentWinInfo = (iframeRef: React.RefObject<HTMLIFrameElement>) => {
    const clientHeight = window.innerHeight || document.documentElement.clientHeight;

    sendMessage(iframeRef, MessageType.Info, {
        url: document.location.href,
        cookies: '',
        devicePixelRatio: window.devicePixelRatio,
        scrollTop: document.documentElement.scrollTop,
        scrollHeight: document.documentElement.scrollHeight,
    });
    sendMessage(iframeRef, MessageType.ClientHeight, clientHeight);

    window.addEventListener(
        'resize',
        () => {
            const clientHeight = window.innerHeight || document.documentElement.clientHeight;
            sendMessage(iframeRef, MessageType.ClientHeight, clientHeight);
        },
        false
    );
};

const initLocationListener = (iframeRef: React.RefObject<HTMLIFrameElement>) => {
    const { addEventListener, location } = window;
    const oldLocation = { ...location };

    const listener = (event: Event, oldLocation: Location) => {
        sendMessage(iframeRef, MessageType.History, {
            oldLocation: JSON.stringify(oldLocation),
            location: JSON.stringify(location),
        });
    };

    addEventListener('hashchange', (event) => listener(event, oldLocation), false);
};

let currentLocation = typeof window === 'undefined' ? '' : window.location.href;

const Uxfeedback: FC<UxfeedbackProps & PropsWithChildren> = ({ iframeSrc, iframeOrigin, sendEventsOnLoad = [] }) => {
    const [showPortal, setShowPortal] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const uxFeedbackReady = useRef<boolean>(false);
    const sendEventsOnLoadRef = useRef<string[]>(sendEventsOnLoad);
    const employerId = useSelector((state) => state?.employer?.id);
    const employerManagerId = useSelector((state) => state?.employerManager?.id);

    const sendMessagesOnLoad = () => {
        sendEventsOnLoadRef.current.forEach((event) => {
            sendMessage(iframeRef, MessageType.Event, { event });
        });
    };

    const location = useLocation();
    useEffect(() => {
        const newLocation = window.location.href;
        sendMessage(iframeRef, MessageType.History, {
            oldLocation: currentLocation,
            location: newLocation,
        });
        currentLocation = newLocation;
    }, [location]);

    const scrollPageListener = useCallback(() => {
        sendMessage(iframeRef, MessageType.Scroll, { scrollTop: document.documentElement.scrollTop });
    }, [iframeRef]);

    const onMessage = useCallback(
        (e: MessageEvent<MessageData>) => {
            const { type = '', payload } = e.data;
            const [prefix, name] = type.split('/');

            if (e.origin !== iframeOrigin || prefix !== 'uxfb') {
                return;
            }

            if (name === MessageType.Style && isString(payload)) {
                setIframeStyle(iframeRef, payload);
                return;
            }

            if (name === MessageType.Opacity && isNumber(payload)) {
                setIframeOpacity(iframeRef, payload);
                return;
            }

            if (name === MessageType.Class && isString(payload)) {
                addClassToIframe(iframeRef, 'uxs-slide-back');
                addClassToIframe(iframeRef, payload);
                return;
            }

            if (name === MessageType.Image && isSize(payload)) {
                const { width = '', height = '' } = payload;
                showWidgetIcon(iframeRef, width, height);
                return;
            }

            if (name === MessageType.Size && isSize(payload)) {
                const { width = '', height = '' } = payload;
                setIframeSize(iframeRef, width, height);
                return;
            }

            if (name === MessageType.Position && isSize(payload)) {
                setIframePosition(iframeRef, payload.height || '');
                return;
            }

            if (name === MessageType.Listener) {
                sendCurrentWinInfo(iframeRef);
                uxFeedbackReady.current = true;
                sendMessage(iframeRef, MessageType.Properties, {
                    employerId: String(employerId),
                    employerManagerId: String(employerManagerId),
                });
                sendMessagesOnLoad();
                return;
            }

            if (name === MessageType.LocationListener) {
                initLocationListener(iframeRef);
                return;
            }

            if (name === MessageType.AddScroll) {
                window.addEventListener('scroll', scrollPageListener);
                return;
            }

            if (name === MessageType.RemoveScroll) {
                window.removeEventListener('scroll', scrollPageListener);
            }
        },
        [iframeRef, iframeOrigin, scrollPageListener, employerId, employerManagerId]
    );

    const onOpenMessage = useCallback(
        (e: Event) => {
            const event = (e as CustomEvent).detail as string;
            if (uxFeedbackReady.current) {
                sendMessage(iframeRef, MessageType.Event, { event });
            } else {
                sendEventsOnLoadRef.current.push(event);
            }
        },
        [uxFeedbackReady, sendEventsOnLoadRef]
    );

    const uxFeedbackFrame = useMemo(
        () => (
            <iframe
                className={styles.emptyFrameHidden}
                id="uxfb_iframe"
                src={iframeSrc}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                ref={iframeRef}
            />
        ),
        [iframeSrc]
    );

    useLayoutEffect(() => {
        if (document.body) {
            setShowPortal(true);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('message', onMessage);
        window.addEventListener('HH-UXFeedback-Open', onOpenMessage);
        return () => {
            window.removeEventListener('message', onMessage);
            window.removeEventListener('scroll', scrollPageListener);
            window.removeEventListener('HH-UXFeedback-Open', onOpenMessage);
        };
    }, [iframeOrigin, iframeRef, onMessage, scrollPageListener, onOpenMessage]);

    if (process.env.SSR) {
        return null;
    }

    return <>{showPortal ? createPortal(uxFeedbackFrame, document.body) : null}</>;
};

export default Uxfeedback;
